import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { retry, timer } from 'rxjs';

export interface RetryConfig {
  maxRetries: number;
  delayMs: number;
  retryStatuses: number[];
}

const DEFAULT_CONFIG: RetryConfig = {
  maxRetries: 2,
  delayMs: 1000,
  retryStatuses: [0, 408, 429, 500, 502, 503, 504] // Network error, timeout, too many requests, server errors
};

/**
 * Interceptor que reintenta automáticamente peticiones HTTP fallidas.
 * Solo reintenta errores de red y errores de servidor (5xx).
 * No reintenta errores de cliente (4xx) excepto 408 (timeout) y 429 (rate limit).
 */
export const retryInterceptor: HttpInterceptorFn = (req, next) => {
  // No reintentar peticiones POST, PUT, DELETE que no son idempotentes
  // a menos que el header X-Retry-Safe esté presente
  const isIdempotent = ['GET', 'HEAD', 'OPTIONS'].includes(req.method);
  const isRetrySafe = req.headers.has('X-Retry-Safe');

  if (!isIdempotent && !isRetrySafe) {
    return next(req);
  }

  return next(req).pipe(
    retry({
      count: DEFAULT_CONFIG.maxRetries,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        // Solo reintentar si el status está en la lista de reintentos
        if (!DEFAULT_CONFIG.retryStatuses.includes(error.status)) {
          throw error;
        }

        // Backoff exponencial: 1s, 2s, 4s...
        const delayTime = DEFAULT_CONFIG.delayMs * Math.pow(2, retryCount - 1);
        return timer(delayTime);
      }
    })
  );
};

/**
 * Helper para marcar una petición como segura para reintentar
 * Útil para POST/PUT/DELETE idempotentes
 */
export function withRetrySafe<T>(options: { headers?: { [key: string]: string } } = {}): { headers: { [key: string]: string } } {
  return {
    ...options,
    headers: {
      ...options.headers,
      'X-Retry-Safe': 'true'
    }
  };
}
